import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import {
  ChevronLeft, RefreshCw, MapPin, Calendar, Droplets, Wind,
  Phone, AlertTriangle, X, ListChecks, Wallet, Globe,
  MessageSquare, Sunrise, Utensils, BedDouble, CreditCard,
  Clock, Navigation, Plane
} from "lucide-react";
import { toast } from "sonner";
import { FlightSheet } from "../components/trip/flights/FlightSheet";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA  = { fontFamily: "'Lora', Georgia, serif" };

// ── Weather icon mapping ──────────────────────────────────────────────────────
function WeatherIcon({ iconCode, size = 48 }) {
  const url = `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
  return <img src={url} alt="météo" style={{ width: size, height: size, filter: "brightness(1.1)" }} />;
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const labels = { en_cours: "En voyage", a_venir: "À venir", termine: "Terminé" };
  const colors = {
    en_cours: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    a_venir:  "bg-amber-500/20 text-amber-300 border-amber-500/30",
    termine:  "bg-stone-500/20 text-stone-300 border-stone-500/30",
  };
  return (
    <span
      className={`text-xs border px-2 py-0.5 rounded-full ${colors[status] || colors.a_venir}`}
      style={LORA}
    >
      {labels[status] || status}
    </span>
  );
}

// ── Weather block ─────────────────────────────────────────────────────────────
function WeatherBlock({ weather, loading, error, onRefresh }) {
  if (loading) {
    return (
      <div className="mx-4 mb-4 rounded-2xl p-5 animate-pulse" style={{ background: "#3A3330" }}>
        <div className="h-4 bg-stone-600 rounded w-32 mb-3" />
        <div className="h-12 bg-stone-600 rounded w-20" />
      </div>
    );
  }
  if (error || !weather) {
    return (
      <div className="mx-4 mb-4 rounded-2xl p-5 border border-stone-700/50" style={{ background: "#3A3330" }}>
        <p className="text-stone-400 text-sm italic" style={LORA}>Météo non disponible</p>
      </div>
    );
  }
  return (
    <div className="mx-4 mb-4 rounded-2xl p-5" style={{ background: "#3A3330" }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-stone-400 text-xs uppercase tracking-widest" style={LORA}>Météo actuelle</span>
        <button onClick={onRefresh} className="text-stone-500 hover:text-[#C17F59] transition-colors p-1">
          <RefreshCw size={13} strokeWidth={1.5} />
        </button>
      </div>
      <div className="flex items-center gap-4">
        <WeatherIcon iconCode={weather.icon_code} size={56} />
        <div className="flex-1">
          <div className="flex items-end gap-2">
            <span className="text-5xl font-light text-white" style={SERIF}>{weather.temp}°</span>
            <span className="text-stone-400 text-sm mb-2" style={LORA}>Ressenti {weather.feels_like}°</span>
          </div>
          <p className="text-stone-300 text-sm capitalize" style={LORA}>{weather.description}</p>
        </div>
        <div className="text-right">
          <p className="text-stone-400 text-xs" style={LORA}>{weather.temp_max}° / {weather.temp_min}°</p>
          <div className="flex items-center justify-end gap-1 mt-1 text-stone-400 text-xs" style={LORA}>
            <Droplets size={11} strokeWidth={1.5} />{weather.humidity}%
          </div>
          <div className="flex items-center justify-end gap-1 mt-0.5 text-stone-400 text-xs" style={LORA}>
            <Wind size={11} strokeWidth={1.5} />{weather.wind_speed} km/h
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Today's program ───────────────────────────────────────────────────────────
function TodayProgram({ program, dayNum, totalDays }) {
  const [expanded, setExpanded] = useState(false);
  if (!program) return null;

  const desc = program.description || "";
  const shortDesc = desc.length > 200 ? desc.slice(0, 200) + "…" : desc;

  return (
    <div className="mx-4 mb-4 rounded-2xl overflow-hidden" style={{ background: "#3A3330" }}>
      {/* Header */}
      <div className="p-5 pb-3">
        <div className="flex items-center gap-2 mb-2">
          <Sunrise size={14} strokeWidth={1.5} className="text-[#C17F59]" />
          <span className="text-[#C17F59] text-xs uppercase tracking-widest" style={LORA}>
            Programme du jour
          </span>
          {totalDays > 0 && (
            <span className="ml-auto text-stone-500 text-xs" style={LORA}>
              {dayNum}/{totalDays}
            </span>
          )}
        </div>
        <h2 className="text-2xl text-white leading-tight" style={{ ...SERIF, fontWeight: 600 }}>
          {program.titre || program.jour || `Jour ${dayNum}`}
        </h2>
      </div>

      {/* Description */}
      <div className="px-5 pb-3">
        <p className="text-stone-300 text-sm leading-relaxed" style={LORA}>
          {expanded ? desc : shortDesc}
        </p>
        {desc.length > 200 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[#C17F59] text-xs mt-2 hover:underline"
            style={LORA}
          >
            {expanded ? "Réduire" : "Lire la suite"}
          </button>
        )}
      </div>

      {/* Details */}
      {(program.hebergement_suggere || program.repas_suggeres || program.budget_jour) && (
        <div className="border-t border-stone-700/50 px-5 py-3 grid grid-cols-1 gap-2.5">
          {program.hebergement_suggere && (
            <div className="flex items-start gap-2">
              <BedDouble size={14} strokeWidth={1.5} className="text-[#C17F59] mt-0.5 flex-shrink-0" />
              <p className="text-stone-300 text-xs leading-relaxed" style={LORA}>{program.hebergement_suggere}</p>
            </div>
          )}
          {program.repas_suggeres && (
            <div className="flex items-start gap-2">
              <Utensils size={14} strokeWidth={1.5} className="text-[#C17F59] mt-0.5 flex-shrink-0" />
              <p className="text-stone-300 text-xs leading-relaxed" style={LORA}>{program.repas_suggeres}</p>
            </div>
          )}
          {program.budget_jour && (
            <div className="flex items-start gap-2">
              <CreditCard size={14} strokeWidth={1.5} className="text-[#C17F59] mt-0.5 flex-shrink-0" />
              <p className="text-stone-300 text-xs" style={LORA}>{program.budget_jour}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Countdown / upcoming ──────────────────────────────────────────────────────
function CountdownBlock({ liveData }) {
  if (!liveData) return null;
  const { trip_status, days_remaining, days_until_departure, current_day_num } = liveData;

  if (trip_status === "en_cours") {
    if (days_remaining === 0) {
      return (
        <div className="mx-4 mb-4 rounded-2xl p-5 border border-[#C17F59]/30" style={{ background: "#3A3330" }}>
          <p className="text-[#C17F59] text-xs uppercase tracking-widest mb-1" style={LORA}>Dernier jour</p>
          <p className="text-white text-lg" style={{ ...SERIF, fontWeight: 600 }}>
            Savourez chaque instant de ce dernier jour.
          </p>
        </div>
      );
    }
    return (
      <div className="mx-4 mb-4 rounded-2xl p-5" style={{ background: "#3A3330" }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-stone-400 text-xs uppercase tracking-widest mb-1" style={LORA}>Encore</p>
            <p className="text-white" style={{ ...SERIF, fontWeight: 600, fontSize: "3rem", lineHeight: 1 }}>
              {days_remaining}
            </p>
            <p className="text-stone-300 text-sm" style={LORA}>
              jour{days_remaining > 1 ? "s" : ""} de bonheur
            </p>
          </div>
          <div className="text-right">
            <Clock size={32} strokeWidth={1} className="text-[#C17F59]/40 mb-1 ml-auto" />
            <p className="text-stone-500 text-xs" style={LORA}>Jour {current_day_num} en cours</p>
          </div>
        </div>
      </div>
    );
  }

  if (trip_status === "a_venir" && days_until_departure !== null) {
    return (
      <div className="mx-4 mb-4 rounded-2xl p-5" style={{ background: "#3A3330" }}>
        <p className="text-amber-400 text-xs uppercase tracking-widest mb-1" style={LORA}>Départ dans</p>
        <p className="text-white" style={{ ...SERIF, fontWeight: 600, fontSize: "3rem", lineHeight: 1 }}>
          {days_until_departure}
        </p>
        <p className="text-stone-300 text-sm" style={LORA}>
          jour{days_until_departure > 1 ? "s" : ""}
        </p>
      </div>
    );
  }

  if (trip_status === "termine") {
    return (
      <div className="mx-4 mb-4 rounded-2xl p-5 border border-stone-700/50" style={{ background: "#3A3330" }}>
        <p className="text-stone-400 text-xs uppercase tracking-widest mb-1" style={LORA}>Voyage terminé</p>
        <p className="text-stone-300 text-base" style={{ ...SERIF, fontWeight: 600 }}>
          Ces souvenirs vous appartiennent pour toujours.
        </p>
      </div>
    );
  }

  return null;
}

// ── Quick actions bar (sticky bottom) ─────────────────────────────────────────
function QuickActionsBar({ tripId, planType, onEmergency, onFlight }) {
  const hasChatbot = ["serenite"].includes(planType);
  const actions = [
    { icon: Navigation, label: "Carte",       href: "/culture",          testid: "live-action-map"       },
    { icon: ListChecks, label: "Valise",       href: "/checklist",        testid: "live-action-checklist" },
    { icon: Wallet,     label: "Budget",       href: "/budget",           testid: "live-action-budget"    },
    hasChatbot
      ? { icon: MessageSquare, label: "Conseiller", href: `/trip/${tripId}`, testid: "live-action-chat"  }
      : { icon: Globe,  label: "Culture",     href: "/culture",          testid: "live-action-culture"   },
    { icon: Plane,      label: "Mon Vol",      onClick: onFlight,         testid: "live-action-flight"    },
    { icon: AlertTriangle, label: "SOS",      onClick: onEmergency,      testid: "live-action-sos", sos: true },
  ];

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-stone-700/50"
      style={{ background: "#2B2623", maxWidth: 480, margin: "0 auto", left: "50%", transform: "translateX(-50%)", width: "100%" }}
      data-testid="live-quick-actions"
    >
      <div className="flex items-center justify-around px-2 py-3">
        {actions.map((action) => {
          const Icon = action.icon;
          const content = (
            <div
              key={action.testid}
              className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all cursor-pointer ${
                action.sos
                  ? "bg-red-500/10 hover:bg-red-500/20"
                  : "hover:bg-stone-700/50"
              }`}
              data-testid={action.testid}
              onClick={action.onClick}
            >
              <Icon
                size={20}
                strokeWidth={1.5}
                className={action.sos ? "text-red-400" : "text-stone-300"}
              />
              <span
                className={`text-xs ${action.sos ? "text-red-400" : "text-stone-400"}`}
                style={LORA}
              >
                {action.label}
              </span>
            </div>
          );
          if (action.href && !action.onClick) {
            return (
              <Link to={action.href} key={action.testid} style={{ textDecoration: "none" }}>
                <div
                  className="flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl hover:bg-stone-700/50 transition-all"
                  data-testid={action.testid}
                >
                  <Icon size={20} strokeWidth={1.5} className="text-stone-300" />
                  <span className="text-xs text-stone-400" style={LORA}>{action.label}</span>
                </div>
              </Link>
            );
          }
          return content;
        })}
      </div>
      {/* iOS safe area */}
      <div style={{ height: "env(safe-area-inset-bottom, 0px)" }} />
    </div>
  );
}

// ── Emergency bottom sheet ────────────────────────────────────────────────────
function EmergencySheet({ urgences, open, onClose }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const items = urgences ? [
    { label: "Police",       value: urgences.police,             icon: "🚔" },
    { label: "SAMU / Ambulance", value: urgences.samu_ambulance, icon: "🚑" },
    { label: "Pompiers",    value: urgences.pompiers,             icon: "🚒" },
    { label: "Urgences universelles", value: urgences.numero_unique_urgences, icon: "🆘" },
  ].filter(i => i.value) : [];

  function extractPhone(text) {
    if (!text) return null;
    const m = text.match(/[\d\s\-\+\(\)]{3,}/);
    return m ? m[0].replace(/\s/g, "").trim() : null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl overflow-y-auto"
        style={{ background: "#1C1917", maxWidth: 480, margin: "0 auto", left: "50%", transform: "translateX(-50%)", width: "100%", maxHeight: "85vh" }}
        data-testid="emergency-sheet"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-stone-600" />
        </div>
        <div className="px-5 pb-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} strokeWidth={1.5} className="text-red-400" />
              <h2 className="text-white text-xl" style={{ ...SERIF, fontWeight: 600 }}>
                Numéros d'urgence
              </h2>
            </div>
            <button onClick={onClose} className="text-stone-400 hover:text-white p-1">
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          {/* Emergency numbers */}
          {items.length > 0 ? (
            <div className="space-y-3 mb-5">
              {items.map((item) => {
                const phone = extractPhone(item.value);
                return (
                  <div
                    key={item.label}
                    className="flex items-center justify-between p-3 rounded-xl border border-stone-700/50"
                    style={{ background: "#2B2623" }}
                  >
                    <div>
                      <p className="text-stone-400 text-xs mb-0.5" style={LORA}>{item.icon} {item.label}</p>
                      <p className="text-white text-sm font-medium" style={LORA}>{item.value}</p>
                    </div>
                    {phone && (
                      <a
                        href={`tel:${phone}`}
                        className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-1.5 rounded-xl text-sm"
                        style={LORA}
                        data-testid={`emergency-call-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        <Phone size={13} strokeWidth={1.5} /> Appeler
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-stone-400 text-sm italic mb-5" style={LORA}>
              Numéros d'urgence non disponibles hors connexion.
            </p>
          )}

          {/* Embassy */}
          {urgences?.ambassade_france && (
            <div
              className="p-4 rounded-xl border border-[#C17F59]/20"
              style={{ background: "#2B2623" }}
            >
              <p className="text-[#C17F59] text-xs uppercase tracking-widest mb-2" style={LORA}>
                Ambassade de France
              </p>
              <p className="text-stone-300 text-sm leading-relaxed" style={LORA}>
                {urgences.ambassade_france}
              </p>
            </div>
          )}

          {/* Hopital */}
          {urgences?.hopital_reference && (
            <div
              className="mt-3 p-4 rounded-xl border border-stone-700/50"
              style={{ background: "#2B2623" }}
            >
              <p className="text-stone-400 text-xs uppercase tracking-widest mb-2" style={LORA}>
                Hôpital de référence
              </p>
              <p className="text-stone-300 text-sm leading-relaxed" style={LORA}>
                {urgences.hopital_reference}
              </p>
            </div>
          )}
        </div>
        <div style={{ height: "env(safe-area-inset-bottom, 16px)" }} />
      </div>
    </>
  );
}

// ── Non-accessible state ──────────────────────────────────────────────────────
function NonAccessibleState({ tripId }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ background: "#2B2623" }}>
      <Calendar size={40} strokeWidth={1} className="text-[#C17F59]/50 mb-4" />
      <h2 className="text-white text-2xl mb-3" style={{ ...SERIF, fontWeight: 600 }}>
        Mode Voyage non disponible
      </h2>
      <p className="text-stone-400 text-sm mb-6" style={LORA}>
        Ce mode est accessible uniquement pour les voyages payés et validés.
      </p>
      <Link
        to={`/trip/${tripId}`}
        className="px-6 py-3 bg-[#C17F59] text-white rounded-xl text-sm"
        style={LORA}
      >
        Voir mon voyage
      </Link>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function TripLivePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [liveData, setLiveData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);
  const [showFlightSheet, setShowFlightSheet] = useState(false);

  const loadLive = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trips/${id}/live`);
      setLiveData(data);
    } catch {
      toast.error("Impossible de charger les données du voyage");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadWeather = useCallback(async () => {
    setWeatherLoading(true);
    setWeatherError(false);
    try {
      const { data } = await axios.get(`${API}/trips/${id}/weather`);
      setWeather(data);
    } catch {
      setWeatherError(true);
    } finally {
      setWeatherLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadLive();
    loadWeather();
  }, [loadLive, loadWeather]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#2B2623" }}>
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-400 text-sm italic" style={LORA}>Chargement du mode voyage…</p>
        </div>
      </div>
    );
  }

  if (!liveData || liveData.trip_status === "non_accessible") {
    return <NonAccessibleState tripId={id} />;
  }

  const destLabel = liveData.destination_finale || liveData.destination;

  return (
    <div className="min-h-screen" style={{ background: "#2B2623" }} data-testid="trip-live-page">
      {/* ── Sticky header ── */}
      <div
        className="sticky top-0 z-30 border-b border-stone-700/50"
        style={{ background: "#2B2623" }}
        data-testid="live-header"
      >
        <div className="max-w-[480px] mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/trip/${id}`)}
            className="text-stone-400 hover:text-white transition-colors p-1 -ml-1"
            data-testid="live-back-btn"
          >
            <ChevronLeft size={22} strokeWidth={1.5} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <MapPin size={12} strokeWidth={1.5} className="text-[#C17F59] flex-shrink-0" />
              <h1 className="text-white text-lg truncate leading-tight" style={{ ...SERIF, fontWeight: 600 }}>
                {destLabel}
              </h1>
            </div>
            {liveData.current_day_num && (
              <p className="text-stone-500 text-xs pl-4" style={LORA}>
                Jour {liveData.current_day_num}
                {liveData.nb_jours_total > 0 ? ` sur ${liveData.nb_jours_total}` : ""}
              </p>
            )}
          </div>
          <StatusBadge status={liveData.trip_status} />
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="max-w-[480px] mx-auto pb-32 pt-4">

        {/* Weather */}
        <WeatherBlock
          weather={weather}
          loading={weatherLoading}
          error={weatherError}
          onRefresh={loadWeather}
        />

        {/* Today's program (en_cours only) */}
        {liveData.trip_status === "en_cours" && liveData.today_program && (
          <TodayProgram
            program={liveData.today_program}
            dayNum={liveData.current_day_num}
            totalDays={liveData.nb_jours_total}
          />
        )}

        {/* Countdown */}
        <CountdownBlock liveData={liveData} />

        {/* Incontournables (a_venir) */}
        {liveData.trip_status === "a_venir" && liveData.incontournables?.length > 0 && (
          <div className="mx-4 mb-4 rounded-2xl p-5" style={{ background: "#3A3330" }}>
            <p className="text-[#C17F59] text-xs uppercase tracking-widest mb-3" style={LORA}>
              À ne pas manquer
            </p>
            <ul className="space-y-2">
              {liveData.incontournables.slice(0, 4).map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#C17F59] text-xs mt-0.5 flex-shrink-0">0{i + 1}</span>
                  <span className="text-stone-300 text-sm" style={LORA}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Dates summary */}
        {(liveData.date_depart || liveData.date_retour) && (
          <div className="mx-4 mb-4 rounded-2xl p-4 flex items-center gap-3" style={{ background: "#3A3330" }}>
            <Calendar size={16} strokeWidth={1.5} className="text-[#C17F59] flex-shrink-0" />
            <div>
              <p className="text-stone-400 text-xs" style={LORA}>
                {liveData.date_depart} — {liveData.date_retour}
              </p>
              {liveData.nb_jours_total > 0 && (
                <p className="text-stone-300 text-xs" style={LORA}>
                  {liveData.nb_jours_total} jour{liveData.nb_jours_total > 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Itinerary link */}
        <div className="mx-4 mb-4">
          <Link
            to={`/trip/${id}`}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border border-stone-700/50 text-stone-300 hover:border-[#C17F59]/30 hover:text-white transition-all text-sm"
            style={LORA}
            data-testid="live-full-itinerary-link"
          >
            Voir l'itinéraire complet
            <ChevronLeft size={14} strokeWidth={1.5} className="rotate-180" />
          </Link>
        </div>

      </div>

      {/* ── Quick actions bar ── */}
      <QuickActionsBar
        tripId={id}
        planType={liveData.plan_type}
        onEmergency={() => setShowEmergency(true)}
        onFlight={() => setShowFlightSheet(true)}
      />

      {/* ── Emergency sheet ── */}
      <EmergencySheet
        urgences={liveData.urgences}
        open={showEmergency}
        onClose={() => setShowEmergency(false)}
      />

      {/* ── Flight sheet ── */}
      <FlightSheet
        tripId={id}
        defaultDate={liveData.date_depart}
        open={showFlightSheet}
        onClose={() => setShowFlightSheet(false)}
      />
    </div>
  );
}
